import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const INTERVAL_LABELS = {
  monthly: 'Månedlig',
  quarterly: 'Kvartalsvis',
  half_yearly: 'Halvårlig',
  yearly: 'Årlig'
};

export default function MembershipBilling() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('batch');

  // Batch state
  const [members, setMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [batchForm, setBatchForm] = useState({
    amount: '',
    description: 'Kontingent',
    date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    income_account_id: ''
  });
  const [batchResult, setBatchResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [batchSending, setBatchSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Recurring state
  const [recurring, setRecurring] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurForm, setRecurForm] = useState({
    name: 'Kontingent',
    description: 'Kontingent',
    amount: '',
    interval_type: 'yearly',
    income_account_id: '',
    next_run_date: new Date().toISOString().split('T')[0],
    auto_send: false,
    days_until_due: 30
  });
  const [runningId, setRunningId] = useState(null);
  const [editingRecurring, setEditingRecurring] = useState(null);

  const loadData = useCallback(() => {
    Promise.all([
      apiFetch('/api/membership-billing/preview'),
      apiFetch('/api/accounts'),
      apiFetch('/api/membership-billing/recurring'),
      apiFetch('/api/settings')
    ]).then(([m, a, r, s]) => {
      setMembers(m);
      setAccounts(a);
      setRecurring(r);
      const kontingent = a.find(acc => acc.account_number === '1010');
      if (kontingent) {
        setBatchForm(prev => prev.income_account_id ? prev : { ...prev, income_account_id: kontingent.id.toString() });
        setRecurForm(prev => prev.income_account_id ? prev : { ...prev, income_account_id: kontingent.id.toString() });
      }
      // Use default payment days from settings
      const days = s.default_payment_days || 30;
      setBatchForm(prev => ({ ...prev, due_date: new Date(Date.now() + days * 86400000).toISOString().split('T')[0] }));
      setRecurForm(prev => prev.days_until_due === 30 ? { ...prev, days_until_due: days } : prev);
    }).catch(() => {});
  }, [apiFetch]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleAll = () => {
    if (selectedIds.length === members.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(members.map(m => m.id));
    }
  };

  const toggleMember = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBatchCreate = async () => {
    setError(''); setSuccess(''); setBatchResult(null);
    if (!batchForm.amount || parseFloat(batchForm.amount) <= 0) {
      setError('Angiv et beløb.'); return;
    }
    if (selectedIds.length === 0) {
      setError('Vælg mindst ét medlem.'); return;
    }

    setSending(true);
    try {
      const result = await apiFetch('/api/membership-billing/batch', {
        method: 'POST',
        body: { ...batchForm, member_ids: selectedIds }
      });
      setSuccess(result.message);
      setBatchResult(result);
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  const handleBatchSend = async () => {
    if (!batchResult?.invoices) return;
    setError(''); setSuccess('');
    setBatchSending(true);
    try {
      const emailInvoices = batchResult.invoices.filter(i => i.member_email);
      const result = await apiFetch('/api/membership-billing/batch-send', {
        method: 'POST',
        body: { invoice_ids: emailInvoices.map(i => i.id) }
      });
      setSuccess(result.message);
      setBatchResult(null);
    } catch (err) {
      setError(err.message);
    }
    setBatchSending(false);
  };

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await apiFetch('/api/membership-billing/recurring', {
        method: 'POST',
        body: recurForm
      });
      setSuccess('Gentagende opkrævning oprettet.');
      setShowRecurringModal(false);
      setRecurForm(prev => ({ ...prev, name: 'Kontingent', description: 'Kontingent', amount: '', next_run_date: new Date().toISOString().split('T')[0] }));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRunRecurring = async (id) => {
    setError(''); setSuccess('');
    setRunningId(id);
    try {
      const result = await apiFetch(`/api/membership-billing/recurring/${id}/run`, { method: 'POST' });
      setSuccess(result.message);
      loadData();
    } catch (err) {
      setError(err.message);
    }
    setRunningId(null);
  };

  const handleEditRecurring = (r) => {
    setEditingRecurring(r);
    setRecurForm({
      name: r.name,
      description: r.description || '',
      amount: r.amount.toString(),
      interval_type: r.interval_type,
      income_account_id: r.income_account_id ? r.income_account_id.toString() : '',
      next_run_date: new Date(r.next_run_date).toISOString().split('T')[0],
      auto_send: !!r.auto_send,
      days_until_due: r.days_until_due || 30
    });
    setShowRecurringModal(true);
  };

  const handleUpdateRecurring = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await apiFetch(`/api/membership-billing/recurring/${editingRecurring.id}`, {
        method: 'PUT',
        body: { ...recurForm, is_active: editingRecurring.is_active }
      });
      setSuccess('Gentagende opkrævning opdateret.');
      setShowRecurringModal(false);
      setEditingRecurring(null);
      setRecurForm(prev => ({ ...prev, name: 'Kontingent', description: 'Kontingent', amount: '', next_run_date: new Date().toISOString().split('T')[0] }));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Slet denne gentagende opkrævning?')) return;
    try {
      await apiFetch(`/api/membership-billing/recurring/${id}`, { method: 'DELETE' });
      loadData();
    } catch (err) { setError(err.message); }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await apiFetch(`/api/membership-billing/recurring/${schedule.id}`, {
        method: 'PUT',
        body: { ...schedule, is_active: !schedule.is_active }
      });
      loadData();
    } catch (err) { setError(err.message); }
  };

  const incomeAccounts = accounts.filter(a => a.type === 'income');
  const membersWithEmail = members.filter(m => m.email);

  return (
    <div>
      <div className="page-header">
        <h1>Kontingentopkrævning</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Tabs */}
      <div style={{display: 'flex', gap: '0', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0'}}>
        {[['batch', 'Masseudsendelse'], ['recurring', 'Gentagende opkrævninger']].map(([key, label]) => (
          <button key={key}
            style={{
              padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: tab === key ? 700 : 400, fontSize: '0.95rem',
              borderBottom: tab === key ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === key ? 'var(--primary)' : '#64748b',
              marginBottom: '-2px'
            }}
            onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* ===== BATCH TAB ===== */}
      {tab === 'batch' && (
        <div>
          <div className="card" style={{marginBottom: '1.5rem'}}>
            <h3 style={{marginBottom: '1rem'}}>Fakturadetaljer</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Beløb pr. medlem (kr.) *</label>
                <input type="number" className="form-control" value={batchForm.amount}
                  onChange={e => setBatchForm({...batchForm, amount: e.target.value})}
                  placeholder="0,00" step="0.01" min="0.01" autoFocus />
              </div>
              <div className="form-group">
                <label>Beskrivelse</label>
                <input className="form-control" value={batchForm.description}
                  onChange={e => setBatchForm({...batchForm, description: e.target.value})}
                  placeholder="F.eks. Kontingent 2026" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Fakturadato</label>
                <input type="date" className="form-control" value={batchForm.date}
                  onChange={e => setBatchForm({...batchForm, date: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Forfaldsdato</label>
                <input type="date" className="form-control" value={batchForm.due_date}
                  onChange={e => setBatchForm({...batchForm, due_date: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Indtægtskonto</label>
                <select className="form-control" value={batchForm.income_account_id}
                  onChange={e => setBatchForm({...batchForm, income_account_id: e.target.value})}>
                  <option value="">-- Vælg --</option>
                  {incomeAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card" style={{marginBottom: '1.5rem'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3>Vælg medlemmer</h3>
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.85rem', color: '#64748b'}}>
                <span>{selectedIds.length} af {members.length} valgt</span>
                <span>({membersWithEmail.length} har email)</span>
              </div>
            </div>

            <div style={{marginBottom: '0.75rem'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                <input type="checkbox"
                  checked={selectedIds.length === members.length && members.length > 0}
                  onChange={toggleAll} />
                <strong>Vælg alle aktive medlemmer</strong>
              </label>
            </div>

            <div className="table-container" style={{maxHeight: '400px', overflowY: 'auto'}}>
              <table>
                <thead>
                  <tr>
                    <th style={{width: '40px'}}></th>
                    <th>Navn</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id} style={{opacity: m.email ? 1 : 0.6}}>
                      <td>
                        <input type="checkbox"
                          checked={selectedIds.includes(m.id)}
                          onChange={() => toggleMember(m.id)} />
                      </td>
                      <td>{[m.name, m.last_name].filter(Boolean).join(' ')}</td>
                      <td>
                        {m.email || <span style={{color: '#dc2626', fontSize: '0.8rem'}}>Ingen email</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary + actions */}
          {batchForm.amount && selectedIds.length > 0 && (
            <div className="card" style={{marginBottom: '1.5rem', background: '#f0f9ff'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                  <strong>{selectedIds.length} fakturaer</strong> à {parseFloat(batchForm.amount || 0).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.
                  <div style={{fontSize: '1.2rem', fontWeight: 700, marginTop: '0.25rem'}}>
                    I alt: {(selectedIds.length * parseFloat(batchForm.amount || 0)).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr.
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleBatchCreate} disabled={sending}
                  style={{padding: '0.85rem 2rem'}}>
                  {sending ? 'Opretter...' : 'Opret fakturaer'}
                </button>
              </div>
            </div>
          )}

          {/* Batch result */}
          {batchResult && (
            <div className="card" style={{marginBottom: '1.5rem', borderLeft: '4px solid #16a34a'}}>
              <h3 style={{marginBottom: '0.75rem'}}>Fakturaer oprettet</h3>
              <p style={{marginBottom: '1rem'}}>
                {batchResult.count} fakturaer oprettet som kladder.
                {batchResult.invoices.filter(i => i.member_email).length} har email og kan sendes.
              </p>
              <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                <button className="btn btn-primary" onClick={handleBatchSend} disabled={batchSending}>
                  {batchSending ? 'Sender...' : `Send ${batchResult.invoices.filter(i => i.member_email).length} fakturaer via email`}
                </button>
                <button className="btn btn-secondary" onClick={() => navigate('/app/fakturaer')}>
                  Se fakturaer
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== RECURRING TAB ===== */}
      {tab === 'recurring' && (
        <div>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <p style={{color: '#64748b', margin: 0}}>
              Gentagende opkrævninger opretter automatisk kladde-fakturaer til alle aktive medlemmer.
            </p>
            <button className="btn btn-primary" onClick={() => {
              setEditingRecurring(null);
              setRecurForm({ name: 'Kontingent', description: 'Kontingent', amount: '', interval_type: 'yearly', income_account_id: '', next_run_date: new Date().toISOString().split('T')[0], auto_send: false, days_until_due: 30 });
              setShowRecurringModal(true);
            }}>
              + Ny opkrævning
            </button>
          </div>

          {recurring.length === 0 ? (
            <div className="card" style={{textAlign: 'center', padding: '3rem', color: '#64748b'}}>
              <p>Ingen gentagende opkrævninger oprettet endnu.</p>
              <p style={{fontSize: '0.85rem'}}>Opret en for at automatisere kontingentopkrævninger.</p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {recurring.map(r => (
                <div key={r.id} className="card" style={{
                  borderLeft: `4px solid ${r.is_active ? 'var(--primary)' : '#94a3b8'}`
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem'}}>
                    <div>
                      <h3 style={{marginBottom: '0.25rem'}}>
                        {r.name}
                        {!r.is_active && <span style={{fontSize: '0.8rem', color: '#94a3b8', marginLeft: '0.5rem'}}>(Inaktiv)</span>}
                      </h3>
                      <p style={{color: '#64748b', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                        {parseFloat(r.amount).toLocaleString('da-DK', {minimumFractionDigits: 2})} kr. — {INTERVAL_LABELS[r.interval_type]}
                        {r.account_name && ` — ${r.account_number} ${r.account_name}`}
                      </p>
                      <div style={{display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: '#64748b'}}>
                        <span>Næste kørsel: <strong>{new Date(r.next_run_date).toLocaleDateString('da-DK')}</strong></span>
                        {r.last_run_date && <span>Sidst kørt: {new Date(r.last_run_date).toLocaleDateString('da-DK')}</span>}
                        <span>Betalingsfrist: {r.days_until_due} dage</span>
                      </div>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                      <button className="btn btn-primary btn-sm"
                        onClick={() => handleRunRecurring(r.id)}
                        disabled={runningId === r.id}>
                        {runningId === r.id ? 'Kører...' : 'Kør nu'}
                      </button>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => handleEditRecurring(r)}>
                        Rediger
                      </button>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => handleToggleActive(r)}>
                        {r.is_active ? 'Deaktiver' : 'Aktiver'}
                      </button>
                      <button className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRecurring(r.id)}>
                        Slet
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recurring modal */}
          {showRecurringModal && (
            <div className="modal-overlay" onClick={() => { setShowRecurringModal(false); setEditingRecurring(null); }}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <h2>{editingRecurring ? 'Rediger gentagende opkrævning' : 'Ny gentagende opkrævning'}</h2>
                <form onSubmit={editingRecurring ? handleUpdateRecurring : handleCreateRecurring}>
                  <div className="form-group">
                    <label>Navn</label>
                    <input className="form-control" value={recurForm.name}
                      onChange={e => setRecurForm({...recurForm, name: e.target.value})} required />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Beløb (kr.) *</label>
                      <input type="number" className="form-control" value={recurForm.amount}
                        onChange={e => setRecurForm({...recurForm, amount: e.target.value})}
                        step="0.01" min="0.01" required />
                    </div>
                    <div className="form-group">
                      <label>Interval</label>
                      <select className="form-control" value={recurForm.interval_type}
                        onChange={e => setRecurForm({...recurForm, interval_type: e.target.value})}>
                        {Object.entries(INTERVAL_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Beskrivelse (på faktura)</label>
                    <input className="form-control" value={recurForm.description}
                      onChange={e => setRecurForm({...recurForm, description: e.target.value})} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Næste kørsel *</label>
                      <input type="date" className="form-control" value={recurForm.next_run_date}
                        onChange={e => setRecurForm({...recurForm, next_run_date: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Betalingsfrist (dage)</label>
                      <input type="number" className="form-control" value={recurForm.days_until_due}
                        onChange={e => setRecurForm({...recurForm, days_until_due: e.target.value})}
                        min="1" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Indtægtskonto</label>
                    <select className="form-control" value={recurForm.income_account_id}
                      onChange={e => setRecurForm({...recurForm, income_account_id: e.target.value})}>
                      <option value="">-- Vælg --</option>
                      {incomeAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowRecurringModal(false); setEditingRecurring(null); }}>Annuller</button>
                    <button type="submit" className="btn btn-primary">{editingRecurring ? 'Gem' : 'Opret'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
